/**
 * sewerAlias — turning what BQC5 calls a sewer into who gets paid.
 *
 * SEWER IN THE HUB'S BQC5 IS FREE TEXT, and it is the column payroll keys
 * on. Nineteen months of it hold 182 spellings for perhaps 95 people:
 * `bambi` and `BAMBI`, `VQUISO`, `REYSE`, `SOLIDAD`, `REB AY`, initials that
 * tell two Concepcions apart (`CONCEPCION A.` / `CONCEPCION J.`), and 64
 * SLASH PAIRS — `LARGA/TULBA`, `ALCANTARA/CABIGAYAN` — where two people
 * shared a bundle. Plus `OPERATIONS` / `BY OPERATION`, which is the floor
 * finishing something in-house and is not a subcontractor at all.
 *
 * THREE THINGS THIS DOES AND ONE IT REFUSES TO DO:
 *
 *   canonical      upper-case, one space, no stray punctuation — so `bambi`
 *                  and `BAMBI ` are one key without anyone typing an alias.
 *   alias          a stored map from a misspelling to a ledger key. Shipped
 *                  with the typos already seen; extended from the run screen
 *                  when a new one turns up, and stored in the module's own
 *                  settings list so the fix is data, not a deploy.
 *   classify       a slash name is SHARED and an OPERATION name is IN-HOUSE.
 *                  Both are reported, neither is paid automatically.
 *   it never GUESSES. `REYSE` → REYES is stored because a person confirmed
 *                  it; nothing here does edit-distance matching on names that
 *                  decide who receives money. An unknown spelling stays
 *                  unknown, becomes a row on the run screen that says so, and
 *                  is mapped by a person once.
 *
 * Pure and I/O-free.
 */

/** The settings key the stored alias map lives under. */
export const SEWER_ALIASES_KEY = "sewerAliases";

/** alias (canonical form) → ledger key. */
export type SewerAliases = Readonly<Record<string, string>>;

/**
 * Misspellings already seen in the hub's BQC5, each confirmed against the
 * payroll masterlist by the volume behind it (VQUISO is 2 rows next to
 * VEQUISO's 2,547). A person can override any of these from the run screen.
 */
export const SHIPPED_ALIASES: SewerAliases = {
  "VQUISO": "VEQUISO",
  "REYSE": "REYES",
  "SOLIDAD": "SOLEDAD",
  "REB AY": "REBAY",
  "SANLAY": "SANGLAY",
  "LARCA": "LARGA",
  "LARA": "LARGA",
};

/**
 * One spelling, one key: upper-case, runs of whitespace collapsed, and the
 * trailing full stop after an initial kept — `CONCEPCION J.` and
 * `CONCEPCION A.` are two different people and the dot is part of how the
 * sheet tells them apart.
 */
export function canonSewer(raw: string | null | undefined): string {
  return String(raw ?? "")
    .toUpperCase()
    .replace(/\s+/g, " ")
    .replace(/\s*\/\s*/g, "/")
    .trim();
}

export type SewerKind =
  /** Resolved to a ledger key, exactly or through an alias. */
  | "known"
  /** Two names on one row — work somebody has to split by hand. */
  | "shared"
  /** The floor's own work, not a subcontractor's. */
  | "inhouse"
  /** Not blank, not shared, not in-house, and nobody has said who it is. */
  | "unknown"
  /** The cell was empty. */
  | "blank";

export interface ISewerResolution {
  /** The canonical spelling as it appeared. */
  raw: string;
  kind: SewerKind;
  /** Ledger key when `kind` is "known"; the canonical raw otherwise. */
  key: string;
  /** For "shared": the parts, each resolved on its own where possible. */
  parts?: string[];
  /** Whether an alias, rather than an exact match, did the resolving. */
  viaAlias?: boolean;
}

const INHOUSE = /^(BY\s+)?OPERATIONS?$/;

/**
 * The alias target that means "this name is the floor's own sewer". Stored
 * like any other alias (raw spelling → IN-HOUSE), so the run screen's picker
 * can mark a name in-house without a second setting.
 */
export const INHOUSE_KEY = "IN-HOUSE";

/**
 * In-house sewers — on a daily rate, not piece rate, so their QC'd sets are
 * never a payout (Mikko, 2026-09-12). They appear on BQC5 under their own
 * names, alone or sharing a bundle with each other ("LARGA/TULBA"), and the
 * run counts their sets as in-house rather than listing them as unmapped.
 * A stored alias to IN-HOUSE adds a name; the shipped list is the floor as
 * it stands.
 */
export const SHIPPED_INHOUSE: readonly string[] = [
  "REBAY", "BROSAS", "LARGA", "TULBA", "CABIGAYAN", "ALCANTARA", "ORADA",
];

/**
 * Every in-house name: the shipped floor plus anything aliased to IN-HOUSE.
 *
 * REMEMBERED PER ALIAS MAP. `resolveSewer` asks for this on EVERY call, and a
 * run resolves a sewer for every workbook pass — 34,000 calls, each one
 * re-canonicalising every alias twice: 0.9 s of a 1.7 s run at production
 * volume, measured (2026-10-01). The answer only changes when the alias map
 * does, and a new map is a new object (`mergeAliases` builds one), so the
 * object itself is the key. Treat the result as READ-ONLY: it is shared.
 */
const inhouseCache = new WeakMap<object, Set<string>>();
export function inhouseSet(aliases: SewerAliases = SHIPPED_ALIASES): Set<string> {
  const hit = inhouseCache.get(aliases);
  if (hit) return hit;
  const s = new Set(SHIPPED_INHOUSE);
  for (const [k, v] of Object.entries(aliases)) if (canonSewer(v) === INHOUSE_KEY) s.add(canonSewer(k));
  inhouseCache.set(aliases, s);
  return s;
}

/**
 * Who a BQC5 SEWER cell means.
 *
 * `known` is the set of ledger keys a payroll profile exists for (plus the
 * shipped directory) — exact matches resolve against it directly, aliases
 * resolve through the merged alias map. A name that resolves to nothing is
 * `unknown`, not dropped: the run screen lists it with the sets behind it
 * and a way to say who it is.
 */
export function resolveSewer(
  raw: string | null | undefined,
  known: ReadonlySet<string>,
  aliases: SewerAliases = SHIPPED_ALIASES,
): ISewerResolution {
  const c = canonSewer(raw);
  if (!c) return { raw: c, kind: "blank", key: "" };
  if (INHOUSE.test(c)) return { raw: c, kind: "inhouse", key: c };
  const inhouse = inhouseSet(aliases);
  if (c.includes("/")) {
    const resolved = c.split("/").filter(Boolean).map(p => resolveSewer(p, known, aliases));
    // A bundle shared between in-house sewers is in-house work, not a split
    // anybody has to make by hand.
    if (resolved.length > 0 && resolved.every(r => r.kind === "inhouse")) return { raw: c, kind: "inhouse", key: c };
    const parts = resolved.map((r, i) => (r.kind === "known" ? r.key : c.split("/").filter(Boolean)[i]));
    return { raw: c, kind: "shared", key: c, parts };
  }
  const via = aliases[c];
  const target = via ? canonSewer(via) : "";
  // In-house wins over a profile of the same name: the floor's sewers are on
  // a daily rate whatever the masterlist says (Mikko, 2026-09-12).
  if (inhouse.has(c) || (target && inhouse.has(target)) || target === INHOUSE_KEY) {
    return { raw: c, kind: "inhouse", key: target && target !== INHOUSE_KEY ? target : c, viaAlias: !!via };
  }
  if (known.has(c)) return { raw: c, kind: "known", key: c };
  if (via && known.has(target)) return { raw: c, kind: "known", key: target, viaAlias: true };
  // An alias to a key we hold no profile for still names the person; treat
  // it as known so the run shows one row under the right name rather than
  // two under two spellings.
  if (via) return { raw: c, kind: "known", key: target, viaAlias: true };
  return { raw: c, kind: "unknown", key: c };
}

/** Shipped aliases with the stored ones layered over them — a stored entry
 *  wins, and a stored "" removes a shipped alias somebody disagrees with. */
export function mergeAliases(stored: SewerAliases | null | undefined): SewerAliases {
  const out: Record<string, string> = { ...SHIPPED_ALIASES };
  for (const [k, v] of Object.entries(stored ?? {})) {
    const key = canonSewer(k);
    if (!key) continue;
    if (v) out[key] = canonSewer(v); else delete out[key];
  }
  return out;
}

/** Parse the stored JSON. Malformed → no stored aliases, never a crash. */
export function parseStoredAliases(text: string | null | undefined): SewerAliases {
  if (!text) return {};
  try {
    const v: unknown = JSON.parse(text);
    if (!v || typeof v !== "object" || Array.isArray(v)) return {};
    const out: Record<string, string> = {};
    for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
      if (typeof val === "string" && canonSewer(k)) out[canonSewer(k)] = val;
    }
    return out;
  } catch {
    return {};
  }
}
